import * as TabsPrimitive from '@radix-ui/react-tabs';
import { AnimatePresence, motion } from 'framer-motion';
import {
    type HTMLAttributes,
    type ReactNode,
    createContext,
    forwardRef,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react';

import { useFluidHover } from '@/hooks/use-fluid-hover';
import { spring } from '@/lib/springs';
import { cn } from '@/lib/utils';

import { useShape } from '@/lib/shape-context';

interface TabsSubtleContextValue {
    registerTab: (index: number, element: HTMLElement | null) => void;
    hoveredIndex: number | null;
    selectedIndex: number;
}

const TabsSubtleContext = createContext<TabsSubtleContextValue | null>(null);

function useTabsSubtle() {
    const context = useContext(TabsSubtleContext);
    if (!context) {
        throw new Error('useTabsSubtle must be used within a TabsSubtle');
    }
    return context;
}

interface TabsSubtleProps
    extends Omit<HTMLAttributes<HTMLDivElement>, 'onSelect'> {
    children: ReactNode;
    selectedIndex: number;
    onSelect: (index: number) => void;
}

const TabsSubtle = forwardRef<HTMLDivElement, TabsSubtleProps>(
    ({ children, selectedIndex, onSelect, className, ...props }, ref) => {
        const containerRef = useRef<HTMLDivElement | null>(null);
        const isMouseInside = useRef(false);
        const [mouseInside, setMouseInside] = useState(false);
        const shape = useShape();

        const {
            activeIndex: hoveredIndex,
            setActiveIndex: setHoveredIndex,
            itemRects: tabRects,
            handlers,
            registerItem,
            measureItems: measureTabs
        } = useFluidHover(containerRef, { axis: 'x' });

        const tabElementsRef = useRef(new Map<number, HTMLElement>());
        const registerTab = useCallback(
            (index: number, element: HTMLElement | null) => {
                registerItem(index, element);
                if (element) tabElementsRef.current.set(index, element);
                else tabElementsRef.current.delete(index);
            },
            [registerItem]
        );

        useEffect(() => {
            measureTabs();
        }, [measureTabs, children]);

        useEffect(() => {
            const elements = tabElementsRef.current;
            if (elements.size === 0) return;
            const observer = new ResizeObserver(() => measureTabs());
            elements.forEach((element) => observer.observe(element));
            return () => observer.disconnect();
        }, [measureTabs, children]);

        const handleMouseMove = useCallback(
            (event: React.MouseEvent) => {
                isMouseInside.current = true;
                setMouseInside(true);
                handlers.onMouseMove(event);
            },
            [handlers]
        );

        const handleMouseLeave = useCallback(() => {
            isMouseInside.current = false;
            setMouseInside(false);
            handlers.onMouseLeave();
        }, [handlers]);

        const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

        const selectedRect = tabRects[selectedIndex];
        const hoverRect = hoveredIndex !== null ? tabRects[hoveredIndex] : null;
        const focusRect = focusedIndex !== null ? tabRects[focusedIndex] : null;
        const isHoveringSelected = hoveredIndex === selectedIndex;
        const isHovering = hoveredIndex !== null && !isHoveringSelected;

        return (
            <TabsSubtleContext.Provider
                value={{ registerTab, hoveredIndex, selectedIndex }}
            >
                <TabsPrimitive.Root
                    asChild
                    value={String(selectedIndex)}
                    onValueChange={(value) => onSelect(Number(value))}
                    activationMode="manual"
                >
                    <TabsPrimitive.List
                        {...props}
                        ref={(node: HTMLDivElement | null) => {
                            containerRef.current = node;
                            if (typeof ref === 'function') ref(node);
                            else if (ref)
                                (
                                    ref as React.MutableRefObject<HTMLDivElement | null>
                                ).current = node;
                        }}
                        onMouseMove={handleMouseMove}
                        onMouseLeave={handleMouseLeave}
                        onFocus={(event: React.FocusEvent<HTMLDivElement>) => {
                            const indexAttribute = (event.target as HTMLElement)
                                .closest('[data-fluid-hover-index]')
                                ?.getAttribute('data-fluid-hover-index');
                            if (indexAttribute != null) {
                                const index = Number(indexAttribute);
                                setHoveredIndex(index);
                                setFocusedIndex(
                                    (event.target as HTMLElement).matches(
                                        ':focus-visible'
                                    )
                                        ? index
                                        : null
                                );
                            }
                        }}
                        onBlur={(event: React.FocusEvent<HTMLDivElement>) => {
                            if (
                                containerRef.current?.contains(
                                    event.relatedTarget as Node
                                )
                            )
                                return;
                            setFocusedIndex(null);
                            if (isMouseInside.current) return;
                            setHoveredIndex(null);
                        }}
                        className={cn(
                            'relative flex items-center -mx-1 -my-1 px-1 py-1 max-w-[calc(100%_+_0.5rem)] overflow-x-auto select-none scrollbar-hidden',
                            className
                        )}
                    >
                        {selectedRect && (
                            <motion.div
                                className={cn(
                                    'absolute bg-active pointer-events-none',
                                    shape.bg
                                )}
                                initial={false}
                                animate={{
                                    left: selectedRect.left,
                                    width: selectedRect.width,
                                    top: selectedRect.top,
                                    height: selectedRect.height,
                                    opacity: isHovering ? 0.8 : 1
                                }}
                                transition={{
                                    ...spring.moderate,
                                    opacity: { duration: 0.08 }
                                }}
                            />
                        )}

                        <AnimatePresence>
                            {hoverRect && !isHoveringSelected && selectedRect && (
                                <motion.div
                                    className={cn(
                                        'absolute bg-active pointer-events-none',
                                        shape.bg
                                    )}
                                    initial={{
                                        left: selectedRect.left,
                                        width: selectedRect.width,
                                        top: selectedRect.top,
                                        height: selectedRect.height,
                                        opacity: 0
                                    }}
                                    animate={{
                                        left: hoverRect.left,
                                        width: hoverRect.width,
                                        top: hoverRect.top,
                                        height: hoverRect.height,
                                        opacity: 0.4
                                    }}
                                    exit={
                                        !mouseInside
                                            ? {
                                                  left: selectedRect.left,
                                                  width: selectedRect.width,
                                                  top: selectedRect.top,
                                                  height: selectedRect.height,
                                                  opacity: 0,
                                                  transition: {
                                                      ...spring.moderate,
                                                      opacity: {
                                                          duration: 0.06
                                                      }
                                                  }
                                              }
                                            : {
                                                  opacity: 0,
                                                  transition: spring.fast.exit
                                              }
                                    }
                                    transition={{
                                        ...spring.fast,
                                        opacity: { duration: 0.08 }
                                    }}
                                />
                            )}
                        </AnimatePresence>

                        <AnimatePresence>
                            {focusRect && (
                                <motion.div
                                    className={cn(
                                        'absolute z-20 border border-[color:var(--focus-ring,#6B97FF)] pointer-events-none',
                                        shape.focusRing
                                    )}
                                    initial={false}
                                    animate={{
                                        left: focusRect.left - 2,
                                        top: focusRect.top - 2,
                                        width: focusRect.width + 4,
                                        height: focusRect.height + 4
                                    }}
                                    exit={{
                                        opacity: 0,
                                        transition: spring.fast.exit
                                    }}
                                    transition={{
                                        ...spring.fast,
                                        opacity: { duration: 0.08 }
                                    }}
                                />
                            )}
                        </AnimatePresence>

                        {children}
                    </TabsPrimitive.List>
                </TabsPrimitive.Root>
            </TabsSubtleContext.Provider>
        );
    }
);
TabsSubtle.displayName = 'TabsSubtle';

interface TabsSubtleItemProps extends HTMLAttributes<HTMLButtonElement> {
    label: string;
    index: number;
}

const TabsSubtleItem = forwardRef<HTMLButtonElement, TabsSubtleItemProps>(
    ({ label, index, className, ...props }, ref) => {
        const internalRef = useRef<HTMLButtonElement | null>(null);
        const shape = useShape();
        const { registerTab, hoveredIndex, selectedIndex } = useTabsSubtle();

        useEffect(() => {
            registerTab(index, internalRef.current);
            return () => registerTab(index, null);
        }, [index, registerTab]);

        const isSelected = selectedIndex === index;
        const isActive = hoveredIndex === index || isSelected;

        return (
            <TabsPrimitive.Trigger
                {...props}
                ref={(node: HTMLButtonElement | null) => {
                    internalRef.current = node;
                    if (typeof ref === 'function') ref(node);
                    else if (ref)
                        (
                            ref as React.MutableRefObject<HTMLButtonElement | null>
                        ).current = node;
                }}
                value={String(index)}
                data-fluid-hover-index={index}
                className={cn(
                    'relative z-10 flex items-center bg-transparent px-4 border-none outline-none h-9 cursor-pointer',
                    shape.bg,
                    className
                )}
            >
                <span className="inline-grid text-subtitle whitespace-nowrap">
                    <span
                        aria-hidden="true"
                        className="invisible col-start-1 row-start-1 font-semibold"
                    >
                        {label}
                    </span>
                    <span
                        className={cn(
                            'col-start-1 row-start-1 transition-colors duration-80',
                            isSelected ? 'font-semibold' : 'font-normal',
                            isActive
                                ? 'text-foreground'
                                : 'text-muted-foreground'
                        )}
                    >
                        {label}
                    </span>
                </span>
            </TabsPrimitive.Trigger>
        );
    }
);
TabsSubtleItem.displayName = 'TabsSubtleItem';

interface SubtleTab {
    value: string;
    label: string;
}

interface SubtleTabsListProps {
    tabs: SubtleTab[];
    value: string;
    onValueChange: (value: string) => void;
    className?: string;
}

const SubtleTabsList = ({
    tabs,
    value,
    onValueChange,
    className
}: SubtleTabsListProps) => (
    <TabsSubtle
        className={className}
        selectedIndex={Math.max(
            0,
            tabs.findIndex((tab) => tab.value === value)
        )}
        onSelect={(index) => onValueChange(tabs[index].value)}
    >
        {tabs.map((tab, index) => (
            <TabsSubtleItem key={tab.value} index={index} label={tab.label} />
        ))}
    </TabsSubtle>
);

export { SubtleTabsList, TabsSubtle, TabsSubtleItem };
